import { useAuth } from '@agentnet/auth';
import {
  getFirmUnderwriters,
  getMarketPlaceProducts,
  saveFirmUnderwriters,
  toggleMarketPlaceProducts,
} from 'api/admin/integrations-features-api';
import { MarketplaceProduct, MarketplaceProductStatus, FirmUnderwritersResponse } from 'api/admin/types';
import {
  getTitleMonitoringConfig,
  getTitleMonitoringFlyer,
  saveTitleMonitoringConfig,
  uploadTitleMonitoringLogo,
  deleteTitleMonitoringLogo,
} from 'api/titleMonitoring/titleMonitoring-api';
import { TitleMonitoringConfig, TitleMonitoringFlyer } from 'api/titleMonitoring/types';
import { convertFileToBase64 } from 'features/orderManagement/fileUtils';
import {
  ContactValidationResult,
  IdentityVerificationResult,
  ISavePhoneNumberPayload,
  ICancelRequestPayload,
  ICancelRequestResponse,
} from 'api/file/interfaces/get-file-order-insights';
import {
  getPartyList,
  getPartyVerificationFileDetail,
  getWireValidationList,
  submitWireValidation,
  getWireValidationOrderDetail,
  validateContactInfo,
  verifyIdentity,
  cancelPartyVerificationRequest,
} from 'api/orderInsights/order-insights-api';
import { AsyncStatusType } from 'features/files/fincen-reporting/constants';
import { PartyVerificationListResult } from 'features/services/identityVerification/interfaces/partyList';
import {
  WireValidationListResult,
  WireValidationSubmitRequest,
  WireValidationSubmitResponse,
} from 'features/services/wireVerification/interfaces/wireValidation';
import { useContext, useEffect } from 'react';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import { ProfileContext, ProfileContextInterface } from './ProfileContext';
import useAsync from './useAsync';
import { PartyVerificationDrawerProps } from 'features/services/identityVerification/interfaces/PartyDrawer';

export type SaveTitleMonitoringConfigPayload = {
  agentLogoUrl: string | null;
  logoFile?: File | null;
  removeLogo?: boolean;
  includeCoverPageInJacket: boolean;
};

export interface IMarketPlace {
  isMarketPlaceEnabled: boolean;
  fetchMarketPlaceServices: (underwriterCode?: string) => Promise<void>;
  fetchMarketPlaceServicesResults: MarketplaceProduct[] | null;
  fetchMarketPlaceStatus: AsyncStatusType;
  fetchUnderwriterProducts: (underwriterCode: string) => Promise<void>;
  fetchUnderwriterProductsResults: MarketplaceProduct[] | null;
  fetchUnderwriterProductsStatus: AsyncStatusType;
  updateMarketPlaceServiceStatus: (productStatus: MarketplaceProductStatus) => Promise<void>;
  updateServiceStatus: AsyncStatusType;
  doValidateContactInfo: (partyInfo: ISavePhoneNumberPayload, isPartyVerification?: boolean) => Promise<void>;
  validateContactInfoStatus: AsyncStatusType;
  validateContactInfoStatusResults: ContactValidationResult | null;
  enhancedIdentityVerification: (partyInfo: ISavePhoneNumberPayload) => Promise<void>;
  enhancedIdentityVerificationStatusResults: IdentityVerificationResult | null;
  enhancedIdentityVerificationStatus: AsyncStatusType;
  fetchPartyVerificationList: () => Promise<void>;
  fetchPartyVerificationListResults: PartyVerificationListResult[] | null;
  fetchPartyVerificationListStatus: AsyncStatusType;
  getPartyVerificationFileDetail: (fileId: string) => Promise<void>;
  getPartyVerificationFileDetailResults: PartyVerificationDrawerProps['party'] | null;
  getPartyVerificationFileDetailStatus: AsyncStatusType;
  cancelPartyVerification: (payload: ICancelRequestPayload) => Promise<void>;
  cancelPartyVerificationStatus: AsyncStatusType;
  cancelPartyVerificationResult: ICancelRequestResponse | null;
  fetchWireValidationList: () => Promise<void>;
  fetchWireValidationListResults: WireValidationListResult[] | null;
  fetchWireValidationListStatus: AsyncStatusType;
  doSubmitWireValidation: (payload: WireValidationSubmitRequest) => Promise<void>;
  submitWireValidationStatus: AsyncStatusType;
  submitWireValidationResult: WireValidationSubmitResponse | null;
  resetSubmitWireValidation: () => void;
  getWireValidationOrderDetail: (fileId: string, orderId: string) => Promise<void>;
  getWireValidationOrderDetailResults: WireValidationSubmitResponse | null;
  getWireValidationOrderDetailStatus: AsyncStatusType;
  resetAllVerificationData: () => void;
  fetchTitleMonitoringConfig: () => Promise<void>;
  fetchTitleMonitoringConfigResults: TitleMonitoringConfig | null;
  fetchTitleMonitoringConfigStatus: AsyncStatusType;
  fetchTitleMonitoringFlyer: (firmGuid: string) => Promise<void>;
  fetchTitleMonitoringFlyerResults: TitleMonitoringFlyer | null;
  fetchTitleMonitoringFlyerStatus: AsyncStatusType;
  saveTitleMonitoringConfig: (payload: SaveTitleMonitoringConfigPayload) => Promise<void>;
  saveTitleMonitoringConfigStatus: AsyncStatusType;
  saveTitleMonitoringConfigResults: TitleMonitoringConfig | null;
  fetchFirmUnderwriters: () => Promise<void>;
  fetchFirmUnderwritersResults: FirmUnderwritersResponse | null;
  fetchFirmUnderwritersStatus: AsyncStatusType;
  saveFirmUnderwriters: (underwriterCodes: string[]) => Promise<void>;
  saveFirmUnderwritersStatus: AsyncStatusType;
}

export const useMarketPlace = (): IMarketPlace => {
  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;

  const isMarketPlaceEnabled = true;

  const services = async (underwriterCode?: string): Promise<MarketplaceProduct[]> => {
    if (!isMarketPlaceEnabled) {
      return [];
    }
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getMarketPlaceProducts(firmId, token, underwriterCode);
  };

  const {
    execute: fetchMarketPlaceServices,
    status: fetchMarketPlaceStatus,
    value: fetchMarketPlaceServicesResults,
    errors: fetchMarketPlaceErrors,
  } = useAsync<MarketplaceProduct[]>(services, false);

  const underwriterServices = async (underwriterCode: string): Promise<MarketplaceProduct[]> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getMarketPlaceProducts(firmId, token, underwriterCode);
  };

  const {
    execute: fetchUnderwriterProducts,
    status: fetchUnderwriterProductsStatus,
    value: fetchUnderwriterProductsResults,
    errors: fetchUnderwriterProductsErrors,
  } = useAsync<MarketplaceProduct[]>(underwriterServices, false);

  const firmUnderwriters = async (): Promise<FirmUnderwritersResponse> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getFirmUnderwriters(firmId, token);
  };

  const {
    execute: fetchFirmUnderwriters,
    status: fetchFirmUnderwritersStatus,
    value: fetchFirmUnderwritersResults,
    errors: fetchFirmUnderwritersErrors,
  } = useAsync<FirmUnderwritersResponse>(firmUnderwriters, false);

  const saveFirmUnderwritersCall = async (underwriterCodes: string[]): Promise<void> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await saveFirmUnderwriters(firmId, underwriterCodes, token);
  };

  const {
    execute: saveFirmUnderwritersExecute,
    status: saveFirmUnderwritersStatus,
    errors: saveFirmUnderwritersErrors,
  } = useAsync<void>(saveFirmUnderwritersCall, false);

  const updateService = async (productStatus: MarketplaceProductStatus): Promise<MarketplaceProductStatus> => {
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await toggleMarketPlaceProducts({ ...productStatus, firmId }, token);
  };

  const {
    execute: updateMarketPlaceServiceStatus,
    status: updateServiceStatus,
    value: updateServiceStatusResults,
    errors: updateServiceStatusErrors,
  } = useAsync<MarketplaceProductStatus>(updateService, false);

  const titleMonitoringConfig = async (): Promise<TitleMonitoringConfig | null> => {
    const firmId = userFirm?.firmId;
    return await getTitleMonitoringConfig({ firmId });
  };

  const {
    execute: fetchTitleMonitoringConfig,
    status: fetchTitleMonitoringConfigStatus,
    value: fetchTitleMonitoringConfigResults,
  } = useAsync<TitleMonitoringConfig | null>(titleMonitoringConfig, false);

  const titleMonitoringFlyer = async (firmGuid: string): Promise<TitleMonitoringFlyer | null> => {
    const token = await getAccessToken();
    return await getTitleMonitoringFlyer(firmGuid, token);
  };

  const {
    execute: fetchTitleMonitoringFlyer,
    status: fetchTitleMonitoringFlyerStatus,
    value: fetchTitleMonitoringFlyerResults,
    errors: fetchTitleMonitoringFlyerErrors,
  } = useAsync<TitleMonitoringFlyer | null>(titleMonitoringFlyer, false);

  const saveTitleMonitoringConfigCall = async (
    payload: SaveTitleMonitoringConfigPayload,
  ): Promise<TitleMonitoringConfig | null> => {
    const token = await getAccessToken();
    const firmId = Number(userFirm?.firmId);
    let agentLogoUrl = payload.agentLogoUrl;

    if (payload.logoFile) {
      const fileContent = await convertFileToBase64(payload.logoFile);
      const uploaded = await uploadTitleMonitoringLogo(
        { firmId, contentType: payload.logoFile.type, fileContent },
        token,
      );
      if (!uploaded?.agentLogoUrl) {
        throw new Error('Unable to upload the company logo. Please try again.');
      }
      agentLogoUrl = uploaded.agentLogoUrl;
    } else if (payload.removeLogo) {
      const deleted = await deleteTitleMonitoringLogo(firmId, token);
      if (!deleted) {
        throw new Error('Unable to delete the company logo. Please try again.');
      }
      agentLogoUrl = null;
    }

    return await saveTitleMonitoringConfig(
      { firmId, agentLogoUrl, includeCoverPageInJacket: payload.includeCoverPageInJacket },
      token,
    );
  };

  const {
    execute: saveTitleMonitoringConfigExecute,
    status: saveTitleMonitoringConfigStatus,
    value: saveTitleMonitoringConfigResults,
    errors: saveTitleMonitoringConfigErrors,
  } = useAsync<TitleMonitoringConfig | null>(saveTitleMonitoringConfigCall, false);

  const validateContactDetails = async (
    partyInfo: ISavePhoneNumberPayload,
    isPartyVerification = false,
  ): Promise<ContactValidationResult> => {
    const token = await getAccessToken();
    return await validateContactInfo({ ...partyInfo }, isPartyVerification, token);
  };

  const {
    execute: doValidateContactInfo,
    status: validateContactInfoStatus,
    value: validateContactInfoStatusResults,
    errors: validateContactInfoStatusErrors,
    reset: resetValidateContactInfo,
  } = useAsync<ContactValidationResult>(validateContactDetails, false);

  const identityVerification = async (
    partyInfo: ISavePhoneNumberPayload,
    isPartyVerification = false,
  ): Promise<IdentityVerificationResult> => {
    const token = await getAccessToken();
    return await verifyIdentity({ ...partyInfo }, isPartyVerification, token);
  };

  const {
    execute: enhancedIdentityVerification,
    status: enhancedIdentityVerificationStatus,
    value: enhancedIdentityVerificationStatusResults,
    errors: enhancedIdentityVerificationStatusErrors,
    reset: resetEnhancedIdentityVerification,
  } = useAsync<IdentityVerificationResult>(identityVerification, false);

  const partyList = async (): Promise<PartyVerificationListResult[]> => {
    if (!isMarketPlaceEnabled) {
      return [];
    }
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getPartyList(firmId, token);
  };

  const {
    execute: fetchPartyVerificationList,
    status: fetchPartyVerificationListStatus,
    value: fetchPartyVerificationListResults,
    errors: fetchPartyVerificationListErrors,
  } = useAsync<PartyVerificationListResult[]>(partyList, false);

  const partyVerificationFileDetail = async (fileId: string): Promise<PartyVerificationDrawerProps['party']> => {
    const token = await getAccessToken();
    return await getPartyVerificationFileDetail(fileId, token);
  };

  const {
    execute: getPartyVerificationFileDetailExecute,
    status: getPartyVerificationFileDetailStatus,
    value: getPartyVerificationFileDetailResults,
    errors: getPartyVerificationFileDetailErrors,
  } = useAsync<PartyVerificationDrawerProps['party']>(partyVerificationFileDetail, false);
  const wireValidationList = async (): Promise<WireValidationListResult[]> => {
    if (!isMarketPlaceEnabled) {
      return [];
    }
    const token = await getAccessToken();
    const firmId = userFirm?.firmId;
    return await getWireValidationList(firmId, token);
  };

  const {
    execute: fetchWireValidationList,
    status: fetchWireValidationListStatus,
    value: fetchWireValidationListResults,
    errors: fetchWireValidationListErrors,
  } = useAsync<WireValidationListResult[]>(wireValidationList, false);

  const wireValidationSubmit = async (payload: WireValidationSubmitRequest): Promise<WireValidationSubmitResponse> => {
    const token = await getAccessToken();
    return await submitWireValidation(payload, token);
  };

  const {
    execute: doSubmitWireValidation,
    status: submitWireValidationStatus,
    value: submitWireValidationResult,
    errors: submitWireValidationErrors,
    reset: resetSubmitWireValidation,
  } = useAsync<WireValidationSubmitResponse>(wireValidationSubmit, false);

  const WireValidationSubmitResponse = async (
    fileId: string,
    orderId: string,
  ): Promise<WireValidationSubmitResponse> => {
    const token = await getAccessToken();
    return await getWireValidationOrderDetail(fileId, orderId, token);
  };

  const {
    execute: getWireValidationOrderDetailExecute,
    status: getWireValidationOrderDetailStatus,
    value: getWireValidationOrderDetailResults,
    errors: getWireValidationOrderDetailErrors,
  } = useAsync<WireValidationSubmitResponse>(WireValidationSubmitResponse, false);

  const cancelPartyVerification = async (payload: ICancelRequestPayload): Promise<ICancelRequestResponse> => {
    const token = await getAccessToken();
    return await cancelPartyVerificationRequest(payload, token);
  };

  const {
    execute: cancelPartyVerificationExecute,
    status: cancelPartyVerificationStatus,
    value: cancelPartyVerificationResult,
    errors: cancelPartyVerificationErrors,
    reset: resetCancelPartyVerification,
  } = useAsync<ICancelRequestResponse>(cancelPartyVerification, false);

  useEffect(() => {
    if (fetchMarketPlaceStatus === 'error' && fetchMarketPlaceErrors?.length) {
      fetchMarketPlaceErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchMarketPlaceStatus, fetchMarketPlaceErrors]);

  useEffect(() => {
    if (fetchUnderwriterProductsStatus === 'error' && fetchUnderwriterProductsErrors?.length) {
      fetchUnderwriterProductsErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchUnderwriterProductsStatus, fetchUnderwriterProductsErrors]);

  useEffect(() => {
    if (fetchFirmUnderwritersStatus === 'error' && fetchFirmUnderwritersErrors?.length) {
      fetchFirmUnderwritersErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchFirmUnderwritersStatus, fetchFirmUnderwritersErrors]);

  useEffect(() => {
    if (saveFirmUnderwritersStatus === 'error' && saveFirmUnderwritersErrors?.length) {
      saveFirmUnderwritersErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [saveFirmUnderwritersStatus, saveFirmUnderwritersErrors]);

  useEffect(() => {
    if (updateServiceStatus === 'error' && updateServiceStatusErrors?.length) {
      updateServiceStatusErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [updateServiceStatus, updateServiceStatusErrors]);

  useEffect(() => {
    if (validateContactInfoStatus === 'error' && validateContactInfoStatusErrors?.length) {
      validateContactInfoStatusErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [validateContactInfoStatus, validateContactInfoStatusErrors]);

  useEffect(() => {
    if (enhancedIdentityVerificationStatus === 'error' && enhancedIdentityVerificationStatusErrors?.length) {
      enhancedIdentityVerificationStatusErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [enhancedIdentityVerificationStatus, enhancedIdentityVerificationStatusErrors]);

  useEffect(() => {
    if (fetchPartyVerificationListStatus === 'error' && fetchPartyVerificationListErrors?.length) {
      fetchPartyVerificationListErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchPartyVerificationListStatus, fetchPartyVerificationListErrors]);

  useEffect(() => {
    if (getPartyVerificationFileDetailStatus === 'error' && getPartyVerificationFileDetailErrors?.length) {
      getPartyVerificationFileDetailErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [getPartyVerificationFileDetailStatus, getPartyVerificationFileDetailErrors]);

  useEffect(() => {
    if (fetchWireValidationListStatus === 'error' && fetchWireValidationListErrors?.length) {
      fetchWireValidationListErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchWireValidationListStatus, fetchWireValidationListErrors]);

  useEffect(() => {
    if (cancelPartyVerificationStatus === 'error' && cancelPartyVerificationErrors?.length) {
      cancelPartyVerificationErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [cancelPartyVerificationStatus, cancelPartyVerificationErrors]);

  useEffect(() => {
    if (submitWireValidationStatus === 'error' && submitWireValidationErrors?.length) {
      submitWireValidationErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [submitWireValidationStatus, submitWireValidationErrors]);

  useEffect(() => {
    if (getWireValidationOrderDetailStatus === 'error' && getWireValidationOrderDetailErrors?.length) {
      getWireValidationOrderDetailErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [getWireValidationOrderDetailStatus, getWireValidationOrderDetailErrors]);

  useEffect(() => {
    if (fetchTitleMonitoringFlyerStatus === 'error' && fetchTitleMonitoringFlyerErrors?.length) {
      fetchTitleMonitoringFlyerErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [fetchTitleMonitoringFlyerStatus, fetchTitleMonitoringFlyerErrors]);

  useEffect(() => {
    if (saveTitleMonitoringConfigStatus === 'error' && saveTitleMonitoringConfigErrors?.length) {
      saveTitleMonitoringConfigErrors.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [saveTitleMonitoringConfigStatus, saveTitleMonitoringConfigErrors]);

  const resetAllVerificationData = () => {
    resetValidateContactInfo();
    resetEnhancedIdentityVerification();
    resetCancelPartyVerification();
  };

  return {
    isMarketPlaceEnabled,
    fetchMarketPlaceServices,
    fetchMarketPlaceServicesResults,
    fetchMarketPlaceStatus,
    fetchUnderwriterProducts,
    fetchUnderwriterProductsResults,
    fetchUnderwriterProductsStatus,
    updateMarketPlaceServiceStatus,
    updateServiceStatus,
    doValidateContactInfo,
    validateContactInfoStatus,
    validateContactInfoStatusResults,
    enhancedIdentityVerification,
    enhancedIdentityVerificationStatusResults,
    enhancedIdentityVerificationStatus,
    fetchPartyVerificationList,
    fetchPartyVerificationListResults,
    fetchPartyVerificationListStatus,
    getPartyVerificationFileDetail: getPartyVerificationFileDetailExecute,
    getPartyVerificationFileDetailResults,
    getPartyVerificationFileDetailStatus,
    cancelPartyVerification: cancelPartyVerificationExecute,
    cancelPartyVerificationStatus,
    cancelPartyVerificationResult,
    fetchWireValidationList,
    fetchWireValidationListResults,
    fetchWireValidationListStatus,
    doSubmitWireValidation,
    submitWireValidationStatus,
    submitWireValidationResult,
    resetSubmitWireValidation,
    getWireValidationOrderDetail: getWireValidationOrderDetailExecute,
    getWireValidationOrderDetailResults,
    getWireValidationOrderDetailStatus,
    resetAllVerificationData,
    fetchTitleMonitoringConfig,
    fetchTitleMonitoringConfigResults,
    fetchTitleMonitoringConfigStatus,
    fetchTitleMonitoringFlyer,
    fetchTitleMonitoringFlyerResults,
    fetchTitleMonitoringFlyerStatus,
    saveTitleMonitoringConfig: saveTitleMonitoringConfigExecute,
    saveTitleMonitoringConfigStatus,
    saveTitleMonitoringConfigResults,
    fetchFirmUnderwriters,
    fetchFirmUnderwritersResults,
    fetchFirmUnderwritersStatus,
    saveFirmUnderwriters: saveFirmUnderwritersExecute,
    saveFirmUnderwritersStatus,
  };
};
